import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import PageHeader from '../components/layout/PageHeader';
import { FormField } from '../components/shared/form';
import { useAuthContext } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import {
  profileValidationErrors,
  useProfile,
  useUpdateProfile,
} from '../hooks/useProfile';
import type { UserProfile, UserProfileUpdate } from '../services/api';
import { deriveInitials } from '../utils/profileFields';

/**
 * Settings · Profile (task 631) — how the User appears to their teammates.
 *
 * The page opens as a read-only VIEW (kv 2026-08-18): the initials avatar,
 * first/last name and sign-in email as plain values, plus a role card. One
 * Edit button turns *only* the first and last name and the initials into
 * inputs; Save changes stays disabled until one of them actually differs from
 * its saved value, and Cancel returns to the view without saving.
 *
 * Deliberate constraints from the approved settings redesign:
 *  - **No photo upload.** The avatar always shows the User's AMS `initials`
 *    uppercased, exactly as the navbar avatar does — the same stored mark that
 *    signs their Build comments and Activity Log entries (task 1089).
 *  - **Email is never editable.** In view and edit mode alike it renders as a
 *    plain (then locked) value; changing the sign-in email is not supported
 *    in-app. The email comes from the authenticated identity, not the profile.
 *
 * Data sources:
 *  - first/last name, initials and the derived `displayName` are platform data, read and
 *    written through the innovate-service → AMS profile proxy (`GET/PATCH
 *    /api/me/profile`, task 596). Nothing here is read from the JWT, and the
 *    save renders the profile the server returns — `displayName` (and so the
 *    initials) is recomputed server-side and may differ from what was typed.
 *  - the sign-in email and the account role come from the authenticated User
 *    (`useAuthContext`); the role card links role management to the Team page.
 *
 * Stale-name note: the identity token still carries the old `given_name` until
 * the next refresh/sign-in, so the avatar and greeting update from the save
 * response (the profile cache), never from the token.
 */

/** What each account role can do, in one plain sentence for the role card. */
const ROLE_DESCRIPTIONS: Record<string, string> = {
  owner: 'Full access, including team and billing management.',
  admin: 'Manage the account, team, and AI Services — everything except billing ownership.',
  editor: 'Create and edit AI Services and their configuration.',
  viewer: 'Read-only access to the account’s AI Services and runs.',
};

/** Trims a text input, turning an emptied field into an explicit clear. */
const toStoredValue = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

/**
 * The initials the User chose, or `''` while AMS derives them — so the input
 * starts empty with the derived value as its placeholder (task 1089).
 */
const storedInitials = (profile: UserProfile): string =>
  profile.initialsSource === 'explicit' ? profile.initials : '';

/** True when the stored value and the form value mean the same thing. */
const unchanged = (stored: string | null, formValue: string | null): boolean =>
  (stored ?? null) === formValue;

export default function ProfilePage() {
  const toast = useToast();
  const { user } = useAuthContext();
  const { data: profile, isLoading, isError, refetch } = useProfile();
  const updateProfile = useUpdateProfile();

  const email = user?.email ?? '';
  const role = user?.accountRole ?? '';

  const [isEditing, setIsEditing] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  // Empty while AMS derives the initials: the derived value shows as the
  // placeholder, and an empty field saves as `null` (back to derived).
  const [initials, setInitials] = useState('');

  // Seed the inputs from stored state on first load and whenever fresh server
  // state arrives while viewing — never while editing, so a save that returns
  // normalised values re-seeds only after we exit edit mode.
  useEffect(() => {
    if (profile && !isEditing) {
      setFirstName(profile.firstName ?? '');
      setLastName(profile.lastName ?? '');
      setInitials(storedInitials(profile));
    }
  }, [profile, isEditing]);

  const nextFirstName = toStoredValue(firstName);
  const nextLastName = toStoredValue(lastName);
  const nextInitials = toStoredValue(initials);

  const hasChanges = profile
    ? !unchanged(profile.firstName, nextFirstName) ||
      !unchanged(profile.lastName, nextLastName) ||
      !unchanged(storedInitials(profile) || null, nextInitials)
    : false;

  // Per-field messages from the last rejected save, keyed by field name.
  const fieldErrors = useMemo(() => {
    return profileValidationErrors(updateProfile.error).reduce<Record<string, string>>(
      (acc, { field, message }) => {
        acc[field] = message;
        return acc;
      },
      {},
    );
  }, [updateProfile.error]);

  const saveFailedWithoutFields =
    updateProfile.isError && Object.keys(fieldErrors).length === 0;

  /** Only the fields that actually changed are sent; the rest stay put. */
  const buildUpdate = (): UserProfileUpdate => {
    const update: UserProfileUpdate = {};
    if (!profile) return update;
    if (!unchanged(profile.firstName, nextFirstName)) update.firstName = nextFirstName;
    if (!unchanged(profile.lastName, nextLastName)) update.lastName = nextLastName;
    if (!unchanged(storedInitials(profile) || null, nextInitials)) update.initials = nextInitials;
    return update;
  };

  const startEditing = () => {
    if (!profile) return;
    setFirstName(profile.firstName ?? '');
    setLastName(profile.lastName ?? '');
    setInitials(storedInitials(profile));
    updateProfile.reset();
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    if (profile) {
      setFirstName(profile.firstName ?? '');
      setLastName(profile.lastName ?? '');
      setInitials(storedInitials(profile));
    }
    updateProfile.reset();
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!hasChanges || updateProfile.isPending) return;

    updateProfile.mutate(buildUpdate(), {
      onSuccess: (saved) => {
        setIsEditing(false);
        toast.success('Profile saved', `We’ll call you ${saved.displayName}.`);
      },
      onError: (error) => {
        // Per-field reasons render against the inputs; this covers the rest
        // (an unreachable profile service, a transport failure).
        if (profileValidationErrors(error).length === 0) {
          toast.error('Profile not saved', 'Your changes could not be saved. Please try again.');
        }
      },
    });
  };

  const roleDescription = ROLE_DESCRIPTIONS[role];

  return (
    <div>
      <PageHeader
        title="Profile"
        description="How you appear to your teammates across Monarch Works."
      />

      <div className="mt-6 flex max-w-2xl flex-col gap-4">
        {isLoading && (
          <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="profile-loading">
            Loading your profile…
          </p>
        )}

        {isError && !isLoading && (
          <div data-testid="profile-error">
            <p className="text-sm text-red-600 dark:text-red-400">
              Your profile is unavailable right now. Please try again.
            </p>
            <button
              onClick={() => refetch()}
              data-testid="profile-retry"
              className="mt-2 inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium rounded-md transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {profile && (
          <>
            <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6">
              {/* Header: initials avatar + who the platform calls you, Edit top-right. */}
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0">
                  <div
                    className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-indigo-500 text-base font-medium text-white"
                    aria-hidden="true"
                    data-testid="profile-initials"
                  >
                    {profile.initials.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p
                      className="text-lg font-semibold text-gray-900 dark:text-white truncate"
                      data-testid="profile-display-name"
                    >
                      {profile.displayName}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{email}</p>
                  </div>
                </div>

                {!isEditing && (
                  <button
                    type="button"
                    onClick={startEditing}
                    data-testid="profile-edit"
                    className="flex-shrink-0 inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium rounded-md transition-colors"
                  >
                    Edit
                  </button>
                )}
              </div>

              {/* VIEW state */}
              {!isEditing && (
                <div className="mt-6" data-testid="profile-view">
                  <dl className="grid grid-cols-[8rem_1fr] gap-x-4 gap-y-3 text-sm">
                    <dt className="text-gray-500 dark:text-gray-400">First name</dt>
                    <dd
                      className="font-medium text-gray-900 dark:text-white"
                      data-testid="profile-first-name-view"
                    >
                      {profile.firstName || <span className="text-gray-400">Not set</span>}
                    </dd>
                    <dt className="text-gray-500 dark:text-gray-400">Last name</dt>
                    <dd
                      className="font-medium text-gray-900 dark:text-white"
                      data-testid="profile-last-name-view"
                    >
                      {profile.lastName || <span className="text-gray-400">Not set</span>}
                    </dd>
                    <dt className="text-gray-500 dark:text-gray-400">Initials</dt>
                    <dd
                      className="font-medium text-gray-900 dark:text-white"
                      data-testid="profile-initials-view"
                    >
                      {profile.initials}
                      {profile.initialsSource === 'derived' && (
                        <span
                          className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400"
                          data-testid="profile-initials-derived-note"
                        >
                          derived from your name
                        </span>
                      )}
                    </dd>
                    <dt className="text-gray-500 dark:text-gray-400">Email</dt>
                    <dd
                      className="font-medium text-gray-900 dark:text-white break-all"
                      data-testid="profile-email-view"
                    >
                      {email}
                    </dd>
                  </dl>
                  <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                    Your avatar shows your initials.
                  </p>
                </div>
              )}

              {/* EDIT state — only first/last name and initials are inputs; email stays locked. */}
              {isEditing && (
                <form
                  data-testid="profile-edit-form"
                  className="mt-6 space-y-5"
                  onSubmit={handleSubmit}
                >
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <FormField
                      label="First name"
                      name="firstName"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      error={fieldErrors.firstName}
                      disabled={updateProfile.isPending}
                      placeholder="Kraig"
                    />
                    <FormField
                      label="Last name"
                      name="lastName"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      error={fieldErrors.lastName}
                      disabled={updateProfile.isPending}
                      placeholder="Vanderklomp"
                    />
                  </div>

                  <div>
                    <FormField
                      label="Initials"
                      name="initials"
                      value={initials}
                      onChange={(e) => setInitials(e.target.value)}
                      error={fieldErrors.initials}
                      disabled={updateProfile.isPending}
                      maxLength={5}
                      placeholder={
                        profile.initialsSource === 'derived'
                          ? profile.initials
                          : deriveInitials(profile.displayName)
                      }
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Signs your comments and Activity Log entries in Build
                    </p>
                  </div>

                  {/* Locked email — displayed, never editable. */}
                  <div>
                    <label
                      htmlFor="profile-email"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      Email
                    </label>
                    <div className="relative">
                      <input
                        id="profile-email"
                        type="email"
                        value={email}
                        readOnly
                        disabled
                        data-testid="profile-email-locked"
                        className="block w-full px-3 py-2 pr-9 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm sm:text-sm bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                      />
                      <LockClosedIcon
                        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                        aria-hidden="true"
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Your sign-in email can’t be changed.
                    </p>
                  </div>

                  {saveFailedWithoutFields && (
                    <p
                      className="text-sm text-red-600 dark:text-red-400"
                      data-testid="profile-save-error"
                    >
                      Your changes could not be saved. Please try again.
                    </p>
                  )}

                  <div className="flex items-center justify-end gap-3 border-t border-gray-200 dark:border-gray-700 pt-4">
                    <button
                      type="button"
                      onClick={cancelEditing}
                      disabled={updateProfile.isPending}
                      data-testid="profile-cancel"
                      className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={!hasChanges || updateProfile.isPending}
                      data-testid="profile-save"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {updateProfile.isPending ? 'Saving…' : 'Save changes'}
                    </button>
                  </div>
                </form>
              )}
            </section>

            {/* Role card — read-only; role management lives on the Team page. */}
            <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                Your role in this account
              </h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                What you can see and do here is set by your account role.
              </p>
              <div className="mt-4 flex items-center gap-3">
                {role ? (
                  <span
                    className="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-semibold text-indigo-800 dark:bg-indigo-500/20 dark:text-indigo-300"
                    data-testid="profile-role"
                  >
                    {role}
                  </span>
                ) : (
                  <span className="text-sm text-gray-400" data-testid="profile-role">
                    No role assigned
                  </span>
                )}
              </div>
              <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
                {roleDescription ? `${roleDescription} ` : ''}Roles are managed on the{' '}
                <Link
                  to="/settings/team"
                  data-testid="profile-role-team-link"
                  className="font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
                >
                  Team
                </Link>{' '}
                page.
              </p>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
